import { KLAYO_COLORS } from '@/themes';
import { formatEnrolmentStatus } from '@/utilities/enrollmentStatus';
import { Stack, Typography } from '@mui/material';

interface EnrolmentStatusTextProps {
  status?: string | null;
  /** Past due and not passed. A MODIFIER on the status, never a replacement for it. */
  isOverdue?: boolean;
  translate?: (key: string) => string;
}

/**
 * An enrolment's status as the two facts it actually is: the learner's real progress state, plus a
 * red Overdue marker when the due date has passed. Mirrors MyLearningCard on the learner side.
 *
 * Shared by every Training Manager grid so they cannot drift: the per-course Enrolments tab and the
 * per-student courses dialog render the same enrolment, and wiring only one of them left the other
 * showing "Not Started" for a learner who was overdue.
 */
export const EnrolmentStatusText = ({ status, isOverdue, translate }: EnrolmentStatusTextProps) => {
  const { label, overdueLabel } = formatEnrolmentStatus(
    status,
    isOverdue,
    (key) => translate?.(key) ?? key
  );

  return (
    <Stack direction='row' gap={0.5} alignItems='center' flexWrap='wrap'>
      {label ? (
        <Typography variant='body2' color={KLAYO_COLORS.GreyGR900}>
          {label}
        </Typography>
      ) : null}
      {overdueLabel ? (
        <Typography variant='body2' color={KLAYO_COLORS.TouchRedTR500}>
          {label ? `· ${overdueLabel}` : overdueLabel}
        </Typography>
      ) : null}
    </Stack>
  );
};

/**
 * The same value as plain text, for a grid column's `valueGetter`. Sorting, quick-filtering and the
 * grid's own CSV export all read the valueGetter, so it must say what the cell says — otherwise the
 * column sorts by a label the user cannot see.
 */
export const enrolmentStatusSortValue = (
  status: string | null | undefined,
  isOverdue: boolean | undefined,
  translate?: (key: string) => string
): string => {
  const { label, overdueLabel } = formatEnrolmentStatus(
    status,
    isOverdue,
    (key) => translate?.(key) ?? key
  );

  return [label, overdueLabel].filter(Boolean).join(' · ');
};
